import React, { useState, useEffect } from 'react';
import './App.css';
import WelcomeScreen from './WelcomeScreen';
import LocationPermission from './LocationPermission';
import HotelConfirmation from './HotelConfirmation';
import GuestVerification from './GuestVerification';
import OTPConfirmation from './OTPConfirmation';
import ServicesPage from './ServicesPage';
import Dashboard from './Dashboard';
import ConfirmTrip from './ConfirmTrip';
import VehicleSelection from './VehicleSelection';
import BookingSummary from './BookingSummary';
import PaymentMethod from './PaymentMethod';
import BookingConfirmation from './BookingConfirmation';
import RidesPage from './RidesPage';
import RideDetailsPage from './RideDetailsPage';
import RideCancelledPage from './RideCancelledPage';
import RideCompletedPage from './RideCompletedPage';
import RequestDetailsPage from './RequestDetailsPage';
import BookingStatusBar from './BookingStatusBar';
import DriverTrackingPage from './DriverTrackingPage';
import EditHotelPage from './EditHotelPage';
import RazorpayPayment from './components/RazorpayPayment';
import SavedCards from './components/SavedCards';
import { saveAuthSession, getLoggedInUserId, toApiPaymentMethod, bookingIdOf, unwrapBooking, apiErrorMessage, apiRequest, parseResponse } from './utils/api';
import { ridePhaseOf, needsOnlinePayNow, needsCompletedCashPay, RIDE_PHASE } from './utils/rides';

const toRideFromBookingSession = (session) => {
  if (!session) {
    return null;
  }
  const booking = unwrapBooking(session.bookingData) || session.bookingData || {};
  const dateTime = session.date && session.time ? `${session.date}T${session.time}:00` : session.date;
  return {
    ...session,
    ...booking,
    bookingId: bookingIdOf(booking) || booking.id || session.bookingId,
    startLatitude: booking.startLatitude ?? session.pickupLocation?.latitude,
    startLongitude: booking.startLongitude ?? session.pickupLocation?.longitude,
    endLatitude: booking.endLatitude ?? session.dropLocation?.latitude,
    endLongitude: booking.endLongitude ?? session.dropLocation?.longitude,
    startLocation: booking.startLocation || session.pickupPoint,
    endLocation: booking.endLocation || session.dropDestination,
    carRequestTime: booking.carRequestTime || dateTime
  };
};

const App = () => {
  const [showWelcome, setShowWelcome] = useState(false);
  const [showLocationPermission, setShowLocationPermission] = useState(false);
  const [showHotelConfirmation, setShowHotelConfirmation] = useState(false);
  const [showGuestVerification, setShowGuestVerification] = useState(false);
  const [showOTPConfirmation, setShowOTPConfirmation] = useState(false);
  const [showServicesPage, setShowServicesPage] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [showConfirmTrip, setShowConfirmTrip] = useState(false);
  const [showVehicleSelection, setShowVehicleSelection] = useState(false);
  const [showBookingSummary, setShowBookingSummary] = useState(false);
  const [showPaymentMethod, setShowPaymentMethod] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [showBookingConfirmation, setShowBookingConfirmation] = useState(false);
  const [showRidesPage, setShowRidesPage] = useState(false);
  const [dashboardTab, setDashboardTab] = useState('HOME');
  const [showRideDetails, setShowRideDetails] = useState(false);
  const [selectedRide, setSelectedRide] = useState(null);
  const [showDriverTracking, setShowDriverTracking] = useState(false);
  const [showBookingStatusBar, setShowBookingStatusBar] = useState(false);
  const [fromNewBooking, setFromNewBooking] = useState(false);
  const [activeBookingData, setActiveBookingData] = useState(null);
  const [showEditHotelPage, setShowEditHotelPage] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [tripData, setTripData] = useState(null);
  const [bookingData, setBookingData] = useState(null);
  const [userLocation, setUserLocation] = useState({ latitude: null, longitude: null });
  const [hotelConfirmFromDashboard, setHotelConfirmFromDashboard] = useState(false);
  const [guestData, setGuestData] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [showRazorpayPayment, setShowRazorpayPayment] = useState(false);
  const [showSavedCards, setShowSavedCards] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [currentBookingForPayment, setCurrentBookingForPayment] = useState(null);
  const [paymentReturnTo, setPaymentReturnTo] = useState(null);

  useEffect(() => {
    // Check for dark mode preference
    const isDark = localStorage.getItem('darkMode') === 'true';
    setDarkMode(isDark);
    if (isDark) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', isDark ? '#121212' : '#764CA2');
  }, []);

  useEffect(() => {
    // Check if user has visited before
    const hasVisited = localStorage.getItem('hasVisited');
    if (!hasVisited) {
      setShowWelcome(true);
    } else {
      // If already visited, show dashboard directly
      setShowDashboard(true);
    }

    // Listen for token expiration event
    const handleTokenExpired = () => {
      // Reset all states
      setShowWelcome(true);
      setShowLocationPermission(false);
      setShowHotelConfirmation(false);
      setShowGuestVerification(false);
      setShowOTPConfirmation(false);
      setShowServicesPage(false);
      setShowDashboard(false);
      setShowConfirmTrip(false);
      setShowVehicleSelection(false);
      setShowBookingSummary(false);
      setShowPaymentMethod(false);
      setShowBookingConfirmation(false);
      setShowRidesPage(false);
      setShowRideDetails(false);
      setShowDriverTracking(false);
      setShowEditHotelPage(false);
      setFromNewBooking(false);
      setShowBookingStatusBar(false);
      setSelectedPaymentMethod(null);
      setSelectedRide(null);
      setSelectedLocation(null);
      setTripData(null);
      setBookingData(null);
      setUserLocation({ latitude: null, longitude: null });
      setGuestData(null);
    };

    window.addEventListener('tokenExpired', handleTokenExpired);

    return () => {
      window.removeEventListener('tokenExpired', handleTokenExpired);
    };
  }, []);

  useEffect(() => {
    if (!showBookingStatusBar) {
      return undefined;
    }
    const hideTimer = window.setTimeout(() => {
      setShowBookingStatusBar(false);
    }, 10000);
    return () => window.clearTimeout(hideTimer);
  }, [showBookingStatusBar]);

  const handleGetStarted = () => {
    // Hide welcome screen and show location permission
    setShowWelcome(false);
    setShowLocationPermission(true);
  };

  const handleAllowAccess = (latitude, longitude) => {
    // Store location and show hotel confirmation
    setUserLocation({ latitude, longitude });
    setHotelConfirmFromDashboard(false);
    setShowLocationPermission(false);
    setShowHotelConfirmation(true);
  };

  const handleChangeHotel = () => {
    let latitude = userLocation.latitude;
    let longitude = userLocation.longitude;
    if (latitude == null || longitude == null) {
      try {
        const saved = JSON.parse(localStorage.getItem('selectedHotel') || '{}');
        latitude = saved.latitude ?? saved.lat ?? null;
        longitude = saved.longitude ?? saved.lng ?? saved.lon ?? null;
      } catch (error) {
        latitude = null;
        longitude = null;
      }
    }
    if (latitude != null && longitude != null) {
      setUserLocation({ latitude: Number(latitude), longitude: Number(longitude) });
    }
    setHotelConfirmFromDashboard(true);
    setShowDashboard(false);
    setShowHotelConfirmation(false);
    setShowEditHotelPage(true);
  };

  const handleConfirmLocation = (hotel) => {
    // Store hotel and show dashboard directly
    if (hotel) {
      localStorage.setItem('selectedHotel', JSON.stringify(hotel));
    }
    setShowHotelConfirmation(false);
    setShowEditHotelPage(false);
    setShowDashboard(true);
  };

  const handleEditHotel = () => {
    setHotelConfirmFromDashboard(false);
    setShowEditHotelPage(true);
  };

  const handleBackFromEditHotel = () => {
    setShowEditHotelPage(false);
    if (hotelConfirmFromDashboard) {
      setHotelConfirmFromDashboard(false);
      setDashboardTab('HOME');
      setShowDashboard(true);
      return;
    }
    setShowHotelConfirmation(true);
  };

  const handleBackFromVerification = () => {
    // Go back to dashboard
    setShowGuestVerification(false);
    setDashboardTab('HOME');
    setShowDashboard(true);
  };

  const handleSendOTP = (guestData) => {
    // Store guest data and show OTP confirmation
    setGuestData(guestData);
    localStorage.setItem('guestData', JSON.stringify(guestData));
    setShowGuestVerification(false);
    setShowOTPConfirmation(true);
  };

  const handleBackFromOTP = () => {
    // Go back to guest verification
    setShowOTPConfirmation(false);
    setShowGuestVerification(true);
  };

  const handleVerifyOTP = (user) => {
    saveAuthSession(user);

    const savedGuest = {
      fullName: user?.name || '',
      email: user?.email || '',
      username: user?.username || '',
      mobileNumber: user?.mobileNumber || guestData?.mobileNumber || '',
      userData: {
        data: {
          id: user?.id,
          username: user?.username,
          email: user?.email,
          role: user?.role,
          name: user?.name,
          fullName: user?.name,
          mobileNumber: user?.mobileNumber,
          imageUrl: user?.imageUrl,
          profileImage: user?.imageUrl,
          executiveId: user?.executiveId,
          vendorId: user?.vendorId,
          token: user?.token,
          accessToken: user?.token
        }
      }
    };

    setGuestData(savedGuest);
    localStorage.setItem('guestData', JSON.stringify(savedGuest));
    localStorage.setItem('hasVisited', 'true');
    localStorage.removeItem('firebaseUser');

    setShowOTPConfirmation(false);

    if (selectedLocation) {
      setShowConfirmTrip(true);
    } else {
      setDashboardTab('HOME');
      setShowDashboard(true);
    }
  };

  const handleNavigateToDashboard = () => {
    // Navigate from services page to dashboard
    setShowServicesPage(false);
    setShowDashboard(true);
  };

  const checkLogin = () => {
    return localStorage.getItem('hasVisited') === 'true';
  };

  const handleLocationSelect = (location) => {
    // When location is selected from dashboard, check login
    setSelectedLocation(location);
    setDashboardTab('HOME');
    if (!checkLogin()) {
      setShowDashboard(false);
      setShowGuestVerification(true);
    } else {
      setShowDashboard(false);
      setShowConfirmTrip(true);
    }
  };

  const handleBackFromTrip = () => {
    // Go back to dashboard
    setShowConfirmTrip(false);
    setDashboardTab('HOME');
    setShowDashboard(true);
    setSelectedLocation(null);
  };

  const handleSelectVehicleFromTrip = (tripData) => {
    // Store trip data and show vehicle selection
    setTripData(tripData);
    setShowConfirmTrip(false);
    setShowVehicleSelection(true);
  };

  const handleBackFromVehicleSelection = () => {
    // Go back to confirm trip
    setShowVehicleSelection(false);
    setShowConfirmTrip(true);
  };

  const handleVehicleSelected = (data) => {
    // After selecting vehicle, show booking summary
    setTripData(data);
    setShowVehicleSelection(false);
    setShowBookingSummary(true);
  };

  const handleBackFromBookingSummary = () => {
    // Go back to vehicle selection
    setShowBookingSummary(false);
    setShowVehicleSelection(true);
  };

  const handleProceedToPayment = async () => {
    // If payment method is not selected, go to payment method page
    if (!selectedPaymentMethod) {
      setShowBookingSummary(false);
      setShowPaymentMethod(true);
      return;
    }

    const isOnlinePayment = selectedPaymentMethod === 'upi' || selectedPaymentMethod === 'card';

    try {
      if (isOnlinePayment) {
        const existingBooking = bookingData?.pendingOnlinePayment ? bookingData.bookingData : null;
        const bookingRecord = bookingIdOf(existingBooking)
          ? existingBooking
          : await handleSubmitRequest(null, true);
        setBookingData({
          ...tripData,
          pendingOnlinePayment: true,
          bookingData: bookingRecord
        });
        setShowBookingSummary(false);
        setShowPaymentMethod(false);
        setShowRazorpayPayment(true);
        return;
      }

      await handleSubmitRequest();
    } catch (err) {
      console.error('Error creating booking:', err);
      alert(err.message || 'Failed to create booking. Please try again.');
    }
  };

  const handleEditPaymentMethod = () => {
    // Go to payment method selection page
    setShowBookingSummary(false);
    setShowPaymentMethod(true);
  };

  const handleSelectPaymentMethod = async (methodId) => {
    // Store selected payment method
    setSelectedPaymentMethod(methodId);
    setSelectedCard(null);
    
    // If paying for completed ride, handle payment based on method
    if (currentBookingForPayment) {
      setShowPaymentMethod(false);
      
      if (methodId === 'cash') {
        // For cash, mark booking as paid via API
        try {
          const bookingId = currentBookingForPayment.bookingId || currentBookingForPayment.id;
          if (bookingId) {
            const updateResponse = await apiRequest(`/bookings/${bookingId}/payment`, {
              method: 'POST',
              body: JSON.stringify({
                paymentMethod: 'CASH'
              })
            });

            if (!updateResponse.ok) {
              throw new Error('Failed to update booking with payment info');
            }

            setCurrentBookingForPayment(null);
            setPaymentReturnTo(null);
            setSelectedPaymentMethod(null);
            setShowRideDetails(false);
            setShowRidesPage(true);
            setShowDashboard(false);
          }
        } catch (err) {
          console.error('Error updating booking with cash payment:', err);
          alert(err.message || 'Failed to mark payment as completed. Please try again.');
          // Stay on payment method page on error
          setShowPaymentMethod(true);
        }
      } else if (methodId === 'upi' || methodId === 'card') {
        // For UPI or Card, show Razorpay payment screen
        setShowRazorpayPayment(true);
      }
    } else {
      // For new bookings, go back to booking summary
      setShowPaymentMethod(false);
      setShowBookingSummary(true);
    }
  };

  const handleViewSavedCards = () => {
    setShowPaymentMethod(false);
    setShowSavedCards(true);
  };

  const handleBackFromSavedCards = () => {
    setShowSavedCards(false);
    setShowPaymentMethod(true);
  };

  const handleSelectCard = (card) => {
    setSelectedCard(card);
    setShowSavedCards(false);
    setShowPaymentMethod(false);
    setSelectedPaymentMethod('card');
    if (currentBookingForPayment) {
      setShowRazorpayPayment(true);
    } else {
      setShowBookingSummary(true);
    }
  };

  const handleAddNewCard = () => {
    setSelectedCard(null);
    setShowSavedCards(false);
    setShowPaymentMethod(false);
    setSelectedPaymentMethod('card');
    if (currentBookingForPayment) {
      setShowRazorpayPayment(true);
    } else {
      setShowBookingSummary(true);
    }
  };

  const restorePaymentOrigin = () => {
    const dest = paymentReturnTo === 'details' && selectedRide ? 'details' : 'rides';
    setShowPaymentMethod(false);
    setShowRazorpayPayment(false);
    setShowSavedCards(false);
    setSelectedPaymentMethod(null);
    setCurrentBookingForPayment(null);
    setPaymentReturnTo(null);
    setShowDashboard(false);
    if (dest === 'details') {
      setShowRideDetails(true);
      setShowRidesPage(false);
      return;
    }
    setShowRideDetails(false);
    setShowRidesPage(true);
  };

  const handlePayNow = (ride) => {
    setPaymentReturnTo(showRideDetails ? 'details' : 'rides');
    setCurrentBookingForPayment(ride);
    setBookingData({
      bookingData: ride,
      selectedVehicle: {
        estimatedTotalCost: ride.fare || 0,
        basePrice: ride.fare || 0
      }
    });
    setShowRideDetails(false);
    setShowRidesPage(false);
    setShowDashboard(false);
    if (needsOnlinePayNow(ride)) {
      setSelectedPaymentMethod('upi');
      setShowPaymentMethod(false);
      setShowRazorpayPayment(true);
      return;
    }
    setSelectedPaymentMethod(null);
    setShowRazorpayPayment(false);
    setShowPaymentMethod(true);
  };

  const handleRazorpaySuccess = async (paymentData) => {
    try {
      // Store context before clearing
      const isCompletedRidePayment = !!currentBookingForPayment;
      
      // Booking is already created, just update with payment info
      // Update booking with payment details via API
      const bookingId = bookingIdOf(bookingData?.bookingData)
        || bookingIdOf(currentBookingForPayment);
      
      if (bookingId) {
        // Razorpay verify already saved paymentId. Keep this update as a follow-up only.
        try {
          await apiRequest(`/bookings/${bookingId}/payment`, {
            method: 'POST',
            body: JSON.stringify({
              paymentId: paymentData.paymentId,
              paymentMethod: 'ONLINE'
            })
          });
        } catch (updateError) {
          console.warn('Payment already verified; booking payment update skipped.', updateError);
        }

        // Clear current booking for payment
        setCurrentBookingForPayment(null);
        setBookingData((current) => {
          if (!current) {
            return current;
          }
          const booking = unwrapBooking(current.bookingData) || current.bookingData || {};
          return {
            ...current,
            pendingOnlinePayment: false,
            bookingData: {
              ...booking,
              paymentId: paymentData.paymentId || booking.paymentId,
              paymentMethod: 'ONLINE',
              paymentStatus: 'PAID'
            }
          };
        });
        
        // Redirect based on context
        setShowRazorpayPayment(false);
        if (isCompletedRidePayment) {
          setPaymentReturnTo(null);
          setShowRidesPage(true);
          setShowDashboard(false);
          setShowRideDetails(false);
        } else {
          setShowDashboard(false);
          setShowBookingSummary(false);
          setShowPaymentMethod(false);
          setShowBookingConfirmation(true);
        }
        
        // Refresh rides to show updated payment status
        // This will be handled by the dashboard/rides page refresh
      } else {
        // Fallback: Redirect based on context
        setCurrentBookingForPayment(null);
        setPaymentReturnTo(null);
        setShowRazorpayPayment(false);
        if (isCompletedRidePayment) {
          setShowRidesPage(true);
          setShowDashboard(false);
          setShowRideDetails(false);
        } else {
          setShowDashboard(false);
          setShowBookingSummary(false);
          setShowPaymentMethod(false);
          setShowBookingConfirmation(true);
        }
      }
    } catch (err) {
      console.error('Error updating booking with payment:', err);
      alert('Payment successful but failed to update booking. Please contact support.');
      const isCompletedRidePayment = !!currentBookingForPayment;
      setCurrentBookingForPayment(null);
      setPaymentReturnTo(null);
      setShowRazorpayPayment(false);
      if (isCompletedRidePayment) {
        setShowRidesPage(true);
        setShowDashboard(false);
        setShowRideDetails(false);
      } else {
        setShowDashboard(false);
        setShowBookingConfirmation(true);
      }
    }
  };

  const handleRazorpayFailure = (error) => {
    console.error('Payment failed:', error);
    alert(error || 'Payment failed. Please try again.');
    setShowRazorpayPayment(false);
    if (currentBookingForPayment) {
      if (needsOnlinePayNow(currentBookingForPayment)) {
        restorePaymentOrigin();
        return;
      }
      setShowPaymentMethod(true);
    } else {
      setShowBookingSummary(true);
    }
  };

  const handleRazorpayCancel = () => {
    setShowRazorpayPayment(false);
    if (currentBookingForPayment) {
      if (needsOnlinePayNow(currentBookingForPayment)) {
        restorePaymentOrigin();
        return;
      }
      setShowPaymentMethod(true);
    } else {
      setShowBookingSummary(true);
    }
  };

  const handleBackFromRazorpay = () => {
    setShowRazorpayPayment(false);
    if (currentBookingForPayment) {
      if (needsOnlinePayNow(currentBookingForPayment)) {
        restorePaymentOrigin();
        return;
      }
      setShowPaymentMethod(true);
    } else {
      setShowBookingSummary(true);
    }
  };

  const handleBackFromPaymentMethod = () => {
    if (currentBookingForPayment) {
      restorePaymentOrigin();
      return;
    }
    setShowPaymentMethod(false);
    setShowBookingSummary(true);
  };

  const handleSubmitRequest = async (paymentData = null, skipConfirmation = false) => {
    // Create booking and redirect to home page
    if (!tripData || !tripData.selectedVehicle || !selectedPaymentMethod) {
      throw new Error('Missing required booking information');
    }

    try {
      const userId = getLoggedInUserId();

      if (!userId) {
        throw new Error('User ID not found. Please complete guest verification first.');
      }

      // Get coordinates from trip data
      const startLatitude = tripData?.pickupLocation?.latitude;
      const startLongitude = tripData?.pickupLocation?.longitude;
      const endLatitude = tripData?.dropLocation?.latitude;
      const endLongitude = tripData?.dropLocation?.longitude;
      const selectedVehicle = tripData.selectedVehicle;
      
      const clientPackageId = selectedVehicle?.clientPackageId
        || selectedVehicle?.client_package_id
        || selectedVehicle?.packageId
        || selectedVehicle?.package_id
        || selectedVehicle?.clientPackage?.id
        || selectedVehicle?.package?.id
        || selectedVehicle?.id
        || null;
      if (!clientPackageId) {
        throw new Error('Package ID not found. Please select a vehicle.');
      }
      
      // Get fare from estimatedTotalCost or basePrice
      const fare = selectedVehicle?.estimatedTotalCost || selectedVehicle?.basePrice || 0;
      
      // Calculate extraCharge if needed (extraKmRate * extraKm + extraHourRate * extraHours)
      // For now, set to 0 if not explicitly provided
      const extraCharge = selectedVehicle?.extraCharge || 0;
      
      // Get distanceKm
      const distanceKm = selectedVehicle?.distanceKm || 0;
      
      // Get location names for startLocation and endLocation
      const startLocation = tripData?.pickupPoint || 'Hotel';
      const endLocation = tripData?.dropDestination || 'Destination';

      // Get date and time from trip data and combine into carRequestTime
      const selectedDate = tripData?.date || null;
      const selectedTime = tripData?.time || null;
      
      // Combine date and time into ISO 8601 format (YYYY-MM-DDTHH:MM:SS)
      let carRequestTime = null;
      if (selectedDate && selectedTime) {
        // Combine date (YYYY-MM-DD) and time (HH:MM) into ISO format
        carRequestTime = `${selectedDate}T${selectedTime}:00`;
      }

      // Validate required fields
      if (!startLatitude || !startLongitude || !endLatitude || !endLongitude) {
        throw new Error('Missing location coordinates');
      }

      const bookingPayload = {
        customerId: userId,
        clientPackageId: clientPackageId,
        fare: Math.round(Number(fare) || 0),
        extraCharge: Number(extraCharge) || 0,
        serviceCharge: 0,
        startLocation: startLocation,
        endLocation: endLocation,
        startLatitude: parseFloat(startLatitude),
        startLongitude: parseFloat(startLongitude),
        endLatitude: parseFloat(endLatitude),
        endLongitude: parseFloat(endLongitude),
        distanceKm: parseFloat(distanceKm) || 0,
        paymentMethod: toApiPaymentMethod(selectedPaymentMethod)
      };

      if (carRequestTime) {
        bookingPayload.carRequestTime = carRequestTime;
      }

      const response = await apiRequest('/bookings', {
        method: 'POST',
        body: JSON.stringify(bookingPayload)
      });

      const bookingResult = await parseResponse(response);

      if (!response.ok || bookingResult?.status === false) {
        throw new Error(apiErrorMessage(bookingResult, 'Failed to create booking'));
      }

      const booking = unwrapBooking(bookingResult);

      // If skipConfirmation is true (for UPI/Card flow), return booking result
      if (skipConfirmation) {
        return booking || bookingResult;
      }

      const session = {
        ...tripData,
        bookingData: booking || bookingResult
      };
      setBookingData(session);
      setShowBookingSummary(false);
      setShowRazorpayPayment(false);
      setShowPaymentMethod(false);
      setShowDashboard(false);
      setShowBookingConfirmation(true);

      return booking || bookingResult;
    } catch (err) {
      console.error('Error creating booking:', err);
      throw err; // Re-throw to let caller handle
    }
  };

  const handleBookingComplete = () => {
    setShowBookingConfirmation(false);
    const ride = toRideFromBookingSession(bookingData);
    if (ride) {
      setSelectedRide(ride);
      setFromNewBooking(true);
      setShowRideDetails(true);
      setShowDashboard(false);
      setShowRidesPage(false);
      return;
    }
    setDashboardTab('HOME');
    setShowDashboard(true);
  };

  const handleNavigateToRides = () => {
    if (!checkLogin()) {
      setShowDashboard(false);
      setShowGuestVerification(true);
      return;
    }
    setShowDashboard(false);
    setShowRidesPage(true);
    // Hide status bar when navigating to rides
    setShowBookingStatusBar(false);
  };

  const handleRequireLogin = () => {
    setShowDashboard(false);
    setShowGuestVerification(true);
  };

  const handleBackFromRides = () => {
    setShowRidesPage(false);
    setShowDashboard(true);
  };

  const handleTrackDriver = (ride) => {
    setSelectedRide(ride);
    setShowDriverTracking(true);
    setShowRideDetails(false);
  };

  const handleBackFromDriverTracking = () => {
    setShowDriverTracking(false);
    setShowRideDetails(true);
  };

  const handleDriverMessage = () => {
    if (!selectedRide) return;
    
    // Try different possible field names for phone number
    // driverMobileNumber is the primary field from the API response
    const phoneNumber = selectedRide?.driverMobileNumber ||
                       selectedRide?.driverPhone || 
                       selectedRide?.driverPhoneNumber || 
                       selectedRide?.phoneNumber || 
                       selectedRide?.phone ||
                       selectedRide?.driver?.phone ||
                       selectedRide?.driver?.phoneNumber ||
                       null;
    
    if (!phoneNumber) {
      alert('Driver phone number not available');
      return;
    }
    
    // Open SMS/messaging app
    // Using sms: protocol which works on both iOS and Android
    window.location.href = `sms:${phoneNumber}`;
  };

  const handleDriverCall = () => {
    if (!selectedRide) return;
    
    // Try different possible field names for phone number
    // driverMobileNumber is the primary field from the API response
    const phoneNumber = selectedRide?.driverMobileNumber ||
                       selectedRide?.driverPhone || 
                       selectedRide?.driverPhoneNumber || 
                       selectedRide?.phoneNumber || 
                       selectedRide?.phone ||
                       selectedRide?.driver?.phone ||
                       selectedRide?.driver?.phoneNumber ||
                       null;
    
    if (!phoneNumber) {
      alert('Driver phone number not available');
      return;
    }
    
    // Open phone dialer
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleRideSelect = async (ride) => {
    const bookingId = bookingIdOf(ride);
    let detailed = ride;
    if (bookingId) {
      try {
        const response = await apiRequest(`/bookings/${bookingId}`, { method: 'GET' });
        const parsed = await parseResponse(response);
        if (response.ok && parsed?.status !== false) {
          detailed = unwrapBooking(parsed) || ride;
        }
      } catch (error) {
        console.warn('Unable to load booking details', error);
      }
    }
    setSelectedRide(detailed);
    setFromNewBooking(false);
    setShowRidesPage(false);
    setShowRideDetails(true);
  };

  const handleBackFromRideDetails = () => {
    setShowRideDetails(false);
    setSelectedRide(null);
    if (fromNewBooking) {
      setFromNewBooking(false);
      setShowRidesPage(false);
      setDashboardTab('HOME');
      setShowDashboard(true);
      if (bookingData) {
        setActiveBookingData(bookingData);
        setShowBookingStatusBar(true);
      }
      setSelectedPaymentMethod(null);
      setSelectedLocation(null);
      return;
    }
    setShowRidesPage(true);
  };

  const handleCancelRequest = async (bookingId) => {
    if (!bookingId) {
      throw new Error('Booking ID is required');
    }
    
    try {
      const response = await apiRequest(`/bookings/${bookingId}/cancel`, {
        method: 'PUT'
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to cancel request');
      }

      const result = await parseResponse(response);

      // Refresh rides list and go back
      setShowRideDetails(false);
      setShowRidesPage(true);
      setSelectedRide(null);
      alert('Request cancelled successfully');
    } catch (err) {
      console.error('Error cancelling request:', err);
      throw err; // Re-throw to let RequestDetailsPage handle the error display
    }
  };

  const handleHelpContact = () => {
    setShowRideDetails(false);
    setSelectedRide(null);
    setShowRidesPage(false);
    setShowDriverTracking(false);
    setDashboardTab('HELP');
    setShowDashboard(true);
  };

  // Show welcome screen on first visit
  if (showWelcome) {
    return <WelcomeScreen onGetStarted={handleGetStarted} />;
  }

  // Show location permission screen after welcome
  if (showLocationPermission) {
    return <LocationPermission onAllowAccess={handleAllowAccess} />;
  }

  // Show edit hotel page (map view) when edit button is clicked
  if (showEditHotelPage) {
    return (
      <EditHotelPage
        latitude={userLocation.latitude}
        longitude={userLocation.longitude}
        onConfirmLocation={handleConfirmLocation}
        onBack={handleBackFromEditHotel}
      />
    );
  }

  // Show hotel confirmation screen after getting location
  if (showHotelConfirmation) {
    return (
      <HotelConfirmation
        latitude={userLocation.latitude}
        longitude={userLocation.longitude}
        onConfirmLocation={handleConfirmLocation}
        onEditHotel={handleEditHotel}
      />
    );
  }

  // Show guest verification screen after confirming hotel
  if (showGuestVerification) {
    return (
      <GuestVerification
        onSendOTP={handleSendOTP}
        onBack={handleBackFromVerification}
      />
    );
  }

  // Show OTP confirmation screen after sending OTP
  if (showOTPConfirmation && guestData) {
    return (
      <OTPConfirmation
        mobileNumber={guestData.mobileNumber}
        countryCode={guestData.countryCode}
        existingUser={guestData.existingUser}
        onVerifyOTP={handleVerifyOTP}
        onBack={handleBackFromOTP}
      />
    );
  }

  // Show services page after successful OTP verification
  if (showServicesPage) {
    return <ServicesPage onNavigateToDashboard={handleNavigateToDashboard} />;
  }

  // Show confirm trip screen when location is selected from dashboard
  if (showConfirmTrip && selectedLocation) {
    return (
      <ConfirmTrip
        selectedLocation={selectedLocation}
        onBack={handleBackFromTrip}
        onSelectVehicle={handleSelectVehicleFromTrip}
      />
    );
  }

  // Show vehicle selection screen after selecting date/time
  if (showVehicleSelection && tripData) {
    return (
      <VehicleSelection
        tripData={tripData}
        onBack={handleBackFromVehicleSelection}
        onSelectVehicle={handleVehicleSelected}
      />
    );
  }

  // Show payment method screen
  if (showPaymentMethod) {
    return (
      <PaymentMethod
        selectedPaymentMethod={selectedPaymentMethod}
        onSelectPaymentMethod={handleSelectPaymentMethod}
        onBack={handleBackFromPaymentMethod}
        onViewSavedCards={handleViewSavedCards}
        onSelectSavedCard={handleSelectCard}
      />
    );
  }

  // Show saved cards screen
  if (showSavedCards) {
    return (
      <SavedCards
        onSelectCard={handleSelectCard}
        onBack={handleBackFromSavedCards}
        onAddNew={handleAddNewCard}
      />
    );
  }

  // Show Razorpay payment screen (only for UPI/Card payments, never for cash)
  if (showRazorpayPayment && selectedPaymentMethod !== 'cash') {
    // Determine amount and booking data based on context
    let amount = 0;
    let bookingDataForPayment = {};
    
    if (currentBookingForPayment) {
      // Payment for completed ride
      amount = currentBookingForPayment.fare || 0;
      bookingDataForPayment = {
        bookingId: bookingIdOf(currentBookingForPayment),
        startLocation: currentBookingForPayment.startLocation || 'Hotel',
        endLocation: currentBookingForPayment.endLocation || 'Destination',
        fare: amount
      };
    } else if (tripData && tripData.selectedVehicle) {
      // Payment during booking flow
      amount = tripData.selectedVehicle?.estimatedTotalCost || tripData.selectedVehicle?.basePrice || 0;
      bookingDataForPayment = {
        startLocation: tripData?.pickupPoint || 'Hotel',
        endLocation: tripData?.dropDestination || 'Destination',
        fare: amount,
        bookingId: bookingIdOf(bookingData?.bookingData)
      };
    }
    
    if (amount > 0) {
      return (
        <RazorpayPayment
          amount={amount}
          bookingData={bookingDataForPayment}
          selectedCard={selectedCard}
          paymentMethod={selectedPaymentMethod}
          onSuccess={handleRazorpaySuccess}
          onFailure={handleRazorpayFailure}
          onCancel={handleRazorpayCancel}
          onBack={handleBackFromRazorpay}
        />
      );
    }
  }

  // Show booking summary screen after selecting vehicle
  if (showBookingSummary && tripData) {
    return (
      <BookingSummary
        tripData={tripData}
        selectedVehicle={tripData.selectedVehicle}
        bookingData={null}
        paymentMethod={selectedPaymentMethod}
        onBack={handleBackFromBookingSummary}
        onProceedToPayment={handleProceedToPayment}
        onEditPaymentMethod={handleEditPaymentMethod}
        onSelectPaymentMethod={handleSelectPaymentMethod}
      />
    );
  }

  // Show booking confirmation screen after successful booking
  if (showBookingConfirmation && bookingData) {
    return (
      <BookingConfirmation
        onComplete={handleBookingComplete}
      />
    );
  }

  // Show driver tracking page
  if (showDriverTracking && selectedRide) {
    return (
      <DriverTrackingPage
        ride={selectedRide}
        onBack={handleBackFromDriverTracking}
        onMessage={handleDriverMessage}
        onCall={handleDriverCall}
      />
    );
  }

  // Show request details page (for active rides), ride cancelled page (for cancelled), ride completed page (for completed), or ride details page (for others)
  if (showRideDetails && selectedRide) {
    const phase = ridePhaseOf(selectedRide);
    const isWaitingPayment = phase === RIDE_PHASE.WAITING_PAYMENT;
    const isActiveRide = phase === RIDE_PHASE.WAITING_VENDOR
      || phase === RIDE_PHASE.VENDOR_ASSIGNED
      || phase === RIDE_PHASE.CAR_ASSIGNED;
    const isCancelled = phase === RIDE_PHASE.CANCELLED;
    const isCompleted = phase === RIDE_PHASE.COMPLETED;
    const openDashboardTab = (tab) => {
      if (tab === 'PROFILE' && !checkLogin()) {
        setShowRideDetails(false);
        setSelectedRide(null);
        setShowGuestVerification(true);
        return;
      }
      setShowRideDetails(false);
      setSelectedRide(null);
      if (tab === 'RIDE') {
        setShowRidesPage(true);
        setShowDashboard(false);
        return;
      }
      setDashboardTab(tab === 'HOME' ? 'HOME' : tab);
      setShowRidesPage(false);
      setShowDashboard(true);
    };

    if (isActiveRide || fromNewBooking) {
      return (
        <RequestDetailsPage
          ride={selectedRide}
          onBack={handleBackFromRideDetails}
          onCancelRequest={handleCancelRequest}
          onHelpContact={handleHelpContact}
          onTrackDriver={handleTrackDriver}
        />
      );
    } else if (isCancelled) {
      return (
        <div className="completed-ride-layout">
          <div className="completed-ride-list">
            <RidesPage
              split
              selectedRideId={selectedRide.bookingId || selectedRide.id}
              onRideSelect={handleRideSelect}
              onPayNow={handlePayNow}
              onBack={handleBackFromRideDetails}
              onNavigateTab={openDashboardTab}
            />
          </div>
          <RideCancelledPage
            ride={selectedRide}
            variant="panel"
            onBack={handleBackFromRideDetails}
          />
        </div>
      );
    } else if (isCompleted) {
      const needsPayment = needsCompletedCashPay(selectedRide);

      return (
        <div className="completed-ride-layout">
          <div className="completed-ride-list">
            <RidesPage
              split
              selectedRideId={selectedRide.bookingId || selectedRide.id}
              onRideSelect={handleRideSelect}
              onPayNow={handlePayNow}
              onBack={handleBackFromRideDetails}
              onNavigateTab={openDashboardTab}
            />
          </div>
          <RideCompletedPage
            ride={selectedRide}
            variant="panel"
            onBack={handleBackFromRideDetails}
            onContactSupport={handleHelpContact}
            onPay={needsPayment ? () => handlePayNow(selectedRide) : null}
            needsPayment={needsPayment}
          />
        </div>
      );
    } else {
      // Show detailed view for other statuses
      return (
        <RideDetailsPage
          ride={selectedRide}
          onBack={handleBackFromRideDetails}
          onPayNow={isWaitingPayment || needsCompletedCashPay(selectedRide) ? handlePayNow : null}
        />
      );
    }
  }

  // Show rides page
  if (showRidesPage) {
    return (
      <RidesPage
        onRideSelect={handleRideSelect}
        onPayNow={handlePayNow}
        onBack={() => {
          setDashboardTab('HOME');
          handleBackFromRides();
        }}
        onNavigateTab={(tab) => {
          if (tab === 'PROFILE' && !checkLogin()) {
            setShowRidesPage(false);
            setShowGuestVerification(true);
            return;
          }
          setDashboardTab(tab);
          setShowRidesPage(false);
          setShowDashboard(true);
        }}
      />
    );
  }

  return (
    <>
      <Dashboard
        onLocationSelect={handleLocationSelect}
        onNavigateToRides={handleNavigateToRides}
        onRideSelect={handleRideSelect}
        isLoggedIn={checkLogin()}
        onRequireLogin={handleRequireLogin}
        onChangeHotel={handleChangeHotel}
        initialTab={dashboardTab}
        onTabChange={setDashboardTab}
      />
      {showBookingStatusBar && activeBookingData && (
        <BookingStatusBar
          bookingData={activeBookingData}
          onClick={() => {
            setShowBookingStatusBar(false);
            setShowDashboard(false);
            setShowRidesPage(true);
          }}
        />
      )}
    </>
  );
};

export default App;
